import React, { useState, useEffect, memo } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  CircularProgress,
  TextField,
  Divider,
  Paper,
  Autocomplete,
} from "@mui/material";
import FactoryIcon from "@mui/icons-material/Factory";
import AgricultureIcon from "@mui/icons-material/Agriculture";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "../../Context/UserContext";
import InputAdornment from "@mui/material/InputAdornment";
import API_URLS from "../../Services/api/action";
import PostApi, { GetApi } from "../../Services/api/ApiCall";
import { useTranslation } from "react-i18next";
import { showSuccess, showError } from "../../utills/toasts";

const textFieldStyles = {
  "& .MuiInputLabel-root": {
    color: "#555",
    "&.Mui-focused": { color: "#1b5e20" },
  },
  "& .MuiInput-underline:before": { borderBottomColor: "#bdbdbd" },
  "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
    borderBottomColor: "#1b5e20",
  },
  "&& .Mui-error": {
    "& .MuiInput-underline:before": { borderBottomColor: "#d32f2f" },
    "& .MuiInputLabel-root": { color: "#d32f2f" },
  },
  "& .MuiFormLabel-asterisk": { color: "red" },
  "& .MuiInputAdornment-root": {
    color: "#333",
    fontWeight: 600,
    marginRight: "8px",
    fontSize: "16px",
  },
};

const MobileInput = memo(
  ({ value, onChange, error, helperText, disabled, label }) => (
    <TextField
      fullWidth
      required
      variant="standard"
      label={label || "10-Digit Mobile Number"}
      value={value}
      onChange={(e) =>
        onChange(e.target.value.replace(/[^0-9]/g, "").slice(0, 10))
      }
      error={error}
      helperText={helperText || " "}
      sx={textFieldStyles}
      disabled={disabled}
      InputProps={{
        startAdornment: <InputAdornment position="start">+91</InputAdornment>,
        inputProps: { maxLength: 10 },
      }}
    />
  ),
);

MobileInput.displayName = "MobileInput";

const Login = () => {
  const { login, token } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roleParam = searchParams.get("role");
  const { t } = useTranslation();

  // ==========================================
  // STATE MANAGEMENT
  // ==========================================
  const [authMode, setAuthMode] = useState("idle");
  const [activeRole, setActiveRole] = useState("");

  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [showOtpInput, setShowOtpInput] = useState(false);

  const [aadhaar, setAadhaar] = useState("");
  const [previewFarmerData, setPreviewFarmerData] = useState(null);
  const [previewLands, setPreviewLands] = useState([]);

  const [regData, setRegData] = useState({
    farmer_name: "",
    mobile_number: "",
    address: "",
    purpose: "",
    district_id: "",
    block_id: "",
    village_id: "",
  });

  const [pcRegData, setPcRegData] = useState({
    username: "",
    mobile: "",
    email: "",
  });

  const [pcUsername, setPcUsername] = useState(
    localStorage.getItem("production_center_username") || "",
  );

  const [errors, setErrors] = useState({});
  const [districts, setDistricts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [villages, setVillages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(120);

  // ==========================================
  // EFFECTS
  // ==========================================
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const res = await GetApi(`${API_URLS.GetDistricts}`, token);
        setDistricts(res);
      } catch (err) {
        console.error("Failed to fetch districts", err);
      }
    };
    fetchDistricts();
  }, []);

  useEffect(() => {
    if (roleParam === "office") {
      setErrors({});
      setActiveRole("Office");
      setAuthMode("otp-login");
      setShowOtpInput(false);
    } else if (roleParam === "production") {
      setErrors({});
      setAuthMode("pc-choice");
    } else if (roleParam === "farmer") {
      setErrors({});
      setAuthMode("farmer-choice");
    }
  }, [roleParam]);

  useEffect(() => {
    let interval;
    if (timer > 0 && showOtpInput) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timer, showOtpInput]);

  // ==========================================
  // FETCH BLOCKS & VILLAGES
  // ==========================================
  const fetchBlocks = async (districtId) => {
    if (!districtId) {
      setBlocks([]);
      setVillages([]);
      return;
    }
    try {
      const res = await GetApi(
        `${API_URLS.GetBlocks}?district_id=${districtId}`,
        token,
      );
      setBlocks(res || []);
      setVillages([]);
    } catch (err) {
      console.error("Failed to fetch blocks", err);
      setBlocks([]);
      setVillages([]);
    }
  };

  const fetchVillages = async (blockId) => {
    if (!blockId) {
      setVillages([]);
      return;
    }
    try {
      const res = await GetApi(
        `${API_URLS.GetVillages}?block_id=${blockId}`,
        token,
      );
      setVillages(res || []);
    } catch (err) {
      console.error("Failed to fetch villages", err);
      setVillages([]);
    }
  };

  // ==========================================
  // BACK BUTTON VISIBILITY LOGIC
  // ==========================================
  const shouldHideBackButton =
    roleParam &&
    (authMode === "farmer-choice" ||
      authMode === "pc-choice" ||
      (authMode === "otp-login" && roleParam === "office"));

  // ==========================================
  // RESET & NAVIGATION FUNCTIONS
  // ==========================================
  const handleBackToHome = () => {
    setAuthMode("idle");
    setActiveRole("");
    setMobile("");
    setOtp("");
    setShowOtpInput(false);
    setAadhaar("");
    setTimer(120);
    setPreviewFarmerData(null);
    setPreviewLands([]);
    setErrors({});
    resetRegData();
  };

  const handleBackToFarmerChoice = () => {
    setAuthMode("farmer-choice");
    setAadhaar("");
    setPreviewFarmerData(null);
    setPreviewLands([]);
    setErrors({});
    resetRegData();
  };

  const resetRegData = () => {
    setRegData({
      farmer_name: "",
      mobile_number: "",
      address: "",
      purpose: "",
      district_id: "",
      block_id: "",
      village_id: "",
    });
    setBlocks([]);
    setVillages([]);
  };

  const handleBack = () => {
    if (
      [
        "farmer-verify-login",
        "farmer-register-verify",
        "farmer-register-form",
        "farmer-confirm-details",
      ].includes(authMode)
    ) {
      handleBackToFarmerChoice();
    } else if (authMode === "pc-register-form") {
      setAuthMode("pc-choice");
    } else if (authMode === "otp-login" && activeRole === "Production Center") {
      setAuthMode("pc-choice");
    } else {
      handleBackToHome();
    }
  };

  const handleCardClick = (cardId) => {
    setErrors({});
    if (cardId === "farmer") {
      setAuthMode("farmer-choice");
    } else if (cardId === "product") {
      setAuthMode("pc-choice");
    } else {
      setActiveRole("Office");
      setAuthMode("otp-login");
      setShowOtpInput(false);
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();

    const newErrors = {};

    if (!mobile) newErrors.mobile = t("login.mobileRequired");
    else if (mobile.length !== 10) newErrors.mobile = t("login.valid10Digit");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    try {
      const roleGroup =
        activeRole === "Production Center" ? "production_center" : "officer";

      await PostApi(
        `${API_URLS.Login}`,
        {
          phone: `+91${mobile}`,
          username: pcUsername,
          expected_role_group: roleGroup,
        },
        token,
      );

      setErrors({});

      setShowOtpInput(true);

      setTimer(120);

      showSuccess("OTP sent successfully!");
    } catch (err) {
      const errorMessage =
        err?.response?.data?.error || err?.message || "Something went wrong";

      showError(errorMessage);

      setErrors({
        mobile: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!otp) newErrors.otp = t("login.otpRequired");
    else if (otp.length < 4) newErrors.otp = t("login.validOtp");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const res = await PostApi(
        `${API_URLS.VerifyOTP}`,
        { phone: `+91${mobile}`, otp: otp },
        token,
      );
      login(res);
      showSuccess(`Logged in successfully as ${activeRole}!`);

      if (res.role === "field_inspector") {
        setTimeout(() => {
          navigate("/inspection");
        }, 100);
      } else {
        setTimeout(() => {
          navigate("/");
        }, 100);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Invalid OTP";
      showError(errorMsg);
      setErrors({ otp: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // FARMER AADHAAR LOGIN
  // ==========================================
  const handleFarmerLogin = async (e) => {
    e.preventDefault();
    const cleanAadhaar = aadhaar.replace(/\D/g, "");
    const newErrors = {};
    if (!cleanAadhaar) newErrors.aadhaar = t("login.aadhaarRequired");
    else if (cleanAadhaar.length !== 12)
      newErrors.aadhaar = t("login.valid12DigitAadhaar");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const res = await PostApi(
        `${API_URLS.GetAadhaar}`,
        { aadhar_no: cleanAadhaar },
        token,
      );
      if (res) {
        login(res);
        showSuccess(`Welcome, ${res.user_name}!`);
        navigate("/");
      } else {
        showError(res.error || "Farmer details incomplete.");
        setErrors({ aadhaar: res.error || "Farmer details incomplete." });
      }
    } catch (err) {
      const errorMsg =
        err.response?.data?.error || t("login.valid12DigitAadhaar");
      showError(errorMsg);
      setErrors({ aadhaar: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // FARMER REGISTRATION - CHECK AADHAAR
  // ==========================================
  const handleCheckAadharForRegistration = async (e) => {
    e.preventDefault();
    const cleanAadhaar = aadhaar.replace(/\D/g, "");
    const newErrors = {};
    if (!cleanAadhaar) newErrors.regAadhaar = t("login.aadhaarRequired");
    else if (cleanAadhaar.length !== 12)
      newErrors.regAadhaar = t("login.valid12DigitAadhaar");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setPreviewFarmerData(null);
    setPreviewLands([]);
    setErrors({});
    try {
      const res = await PostApi(
        `${API_URLS.FarmerAadhaarCard}`,
        { aadhar_no: cleanAadhaar },
        token,
      );
      if (res.status === "success" && res.data?.farmer) {
        setPreviewFarmerData(res.data.farmer);
        setPreviewLands(res.data.lands || []);
        setAuthMode("farmer-confirm-details");
      } else {
        const errorMsg = res.data.error || "Farmer details incomplete.";
        showError(errorMsg);
        setErrors({ regAadhaar: errorMsg });
      }
    } catch (err) {
      setAuthMode("farmer-register-form");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmFarmerRegistration = async () => {
    setLoading(true);
    try {
      const res = await PostApi(
        `${API_URLS.CheckAadhaar}`,
        { aadhar_no: aadhaar.replace(/\D/g, "") },
        token,
      );
      if (res.status === "linked_and_logged_in") {
        login(res);
        showSuccess(res.message);
        navigate("/");
      } else if (res.status === "already_registered") {
        showError(res.message);
        setErrors({ confirm: res.message });
      }
    } catch (err) {
      const errorMsg = err.response?.error || "Failed to confirm registration.";
      showError(errorMsg);
      setErrors({ confirm: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // NON-FARMER REGISTRATION SUBMIT
  // ==========================================
  const handleFarmerRegisterSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!regData.farmer_name) newErrors.farmer_name = t("login.fieldRequired");
    if (!regData.mobile_number)
      newErrors.mobile_number = t("login.mobileRequired");
    else if (regData.mobile_number.length !== 10)
      newErrors.mobile_number = t("login.exactly10Digits");
    if (!regData.district_id) newErrors.district_id = t("login.fieldRequired");
    if (!regData.block_id) newErrors.block_id = t("login.fieldRequired");
    if (!regData.village_id) newErrors.village_id = t("login.fieldRequired");
    if (!regData.address) newErrors.address = t("login.fieldRequired");
    if (!regData.purpose) newErrors.purpose = t("login.fieldRequired");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setErrors({});
    let latitude = null;
    let longitude = null;

    try {
      const selectedDistrict = districts.find(
        (d) => String(d.id) === String(regData.district_id),
      );
      const selectedBlock = blocks.find(
        (b) => String(b.id) === String(regData.block_id),
      );
      const selectedVillage = villages.find(
        (v) => String(v.id) === String(regData.village_id),
      );
      const searchQuery = `${regData.address}, ${selectedVillage?.Village_Name || ""}, ${selectedBlock?.Block_Name || ""}, ${selectedDistrict?.District_Name || ""}, Tamil Nadu, India`;
      try {
        const geoRes = await axios.get(
          "https://nominatim.openstreetmap.org/search",
          { params: { format: "json", q: searchQuery, limit: 1 } },
        );
        if (geoRes.data && geoRes.data.length > 0) {
          latitude = parseFloat(geoRes.data[0].lat);
          longitude = parseFloat(geoRes.data[0].lon);
        }
      } catch (geoErr) {
        console.error("Geocoding error:", geoErr);
      }

      const payload = {
        aadhar_no: aadhaar.replace(/\D/g, ""),
        farmer_name: regData.farmer_name,
        mobile_number: `+91${regData.mobile_number}`,
        address: regData.address,
        purpose: regData.purpose,
        district_id: parseInt(regData.district_id),
        block_id: parseInt(regData.block_id),
        village_id: parseInt(regData.village_id),
        latitude,
        longitude,
      };

      const res = await PostApi(
        `${API_URLS.RegisterNonFarmer}`,
        payload,
        token,
      );
      if (res.status === "success") {
        login(res);
        showSuccess(res.message);
        navigate("/");
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || "Registration failed";
      showError(errorMsg);
      setErrors({ submit: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const handleRegChange = (field, value) => {
    setRegData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleDistrictChange = (e, val) => {
    const districtId = val?.id ? String(val.id) : "";
    setRegData((prev) => ({
      ...prev,
      district_id: districtId,
      block_id: "",
      village_id: "",
    }));
    setBlocks([]);
    setVillages([]);
    if (errors.district_id) setErrors((prev) => ({ ...prev, district_id: "" }));
    if (errors.block_id) setErrors((prev) => ({ ...prev, block_id: "" }));
    if (errors.village_id) setErrors((prev) => ({ ...prev, village_id: "" }));
    if (districtId) {
      fetchBlocks(districtId);
    }
  };

  const handleBlockChange = (e, val) => {
    const blockId = val?.id ? String(val.id) : "";
    setRegData((prev) => ({
      ...prev,
      block_id: blockId,
      village_id: "",
    }));
    setVillages([]);
    if (errors.block_id) setErrors((prev) => ({ ...prev, block_id: "" }));
    if (errors.village_id) setErrors((prev) => ({ ...prev, village_id: "" }));
    if (blockId) {
      fetchVillages(blockId);
    }
  };

  const handleVillageChange = (e, val) => {
    const villageId = val?.id ? String(val.id) : "";
    setRegData((prev) => ({
      ...prev,
      village_id: villageId,
    }));
    if (errors.village_id) setErrors((prev) => ({ ...prev, village_id: "" }));
  };

  const handlePcRegChange = (field, value) => {
    setPcRegData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  // ==========================================
  // HANDLE PC REGISTER
  // ==========================================
  const handlePcRegisterSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};

    if (!pcRegData.mobile) newErrors.mobile = t("login.mobileRequired");
    else if (pcRegData.mobile.length !== 10)
      newErrors.mobile = t("login.exactly10Digits");

    if (!pcRegData.email) newErrors.email = t("login.emailRequired");
    else if (!/\S+@\S+\.\S+/.test(pcRegData.email))
      newErrors.email = t("login.validEmail");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const payloadToSend = {
        ...pcRegData,
        mobile: `+91${pcRegData.mobile}`,
      };

      const res = await PostApi(
        `${API_URLS.RegisterProductionCenter}`,
        payloadToSend,
        token,
      );

      localStorage.setItem("production_center_username", pcRegData.username);

      setPcUsername(pcRegData.username);

      showSuccess(
        res.message || "Registration successful! Please login with OTP.",
      );

      setActiveRole("Production Center");
      setAuthMode("otp-login");
      setShowOtpInput(false);

      setMobile(pcRegData.mobile);
    } catch (error) {
      const backendError = error.message || "An error occurred";

      if (backendError.includes("username")) {
        setErrors({
          username: "This username is already taken.",
        });
      } else {
        setErrors({
          submit: backendError,
        });
      }

      showError(backendError);
    } finally {
      setLoading(false);
    }
  };

  const btnStyle = {
    width: "100%",
    padding: "12px",
    mt: 2,
    background: "linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)",
    color: "white",
    border: "none",
    borderRadius: 8,
    fontWeight: "bold",
    fontSize: "16px",
    cursor: "pointer",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 10,
    transition: "background 0.3s ease",
    "&:hover": {
      background: "linear-gradient(135deg, #153a1e 0%, #1b5e20 100%)",
    },
  };

  const dashboardCards = [
    {
      id: "product",
      title: t("login.productionCenter"),
      icon: <FactoryIcon sx={{ fontSize: 50, color: "#ffc022" }} />,
    },
    {
      id: "farmer",
      title: t("login.farmer"),
      icon: <AgricultureIcon sx={{ fontSize: 50, color: "#ffc022" }} />,
    },
    {
      id: "office",
      title: t("login.aoAao"),
      icon: <CorporateFareIcon sx={{ fontSize: 50, color: "#ffc022" }} />,
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        backgroundImage:
          'linear-gradient(rgba(255, 255, 255, 0.18), rgba(255, 255, 255, 0.2)), url("/banner.jpeg")',
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          p: 3,
        }}
      >
        {authMode === "idle" && !roleParam && (
          <Grid container spacing={4} maxWidth="900px" justifyContent="center">
            {dashboardCards.map((card) => (
              <Grid
                item
                xs={12}
                sm={4}
                key={card.id}
                width="250px"
                height="250px"
              >
                <Box
                  onClick={() => handleCardClick(card.id)}
                  sx={{
                    bgcolor: "white",
                    cursor: "pointer",
                    borderRadius: 4,
                    padding: "3rem 1.5rem",
                    textAlign: "center",
                    transition: "all 0.4s ease",
                    border: "1px solid rgba(255, 192, 34, 0.1)",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
                    "&:hover": {
                      transform: "translateY(-12px)",
                      boxShadow: "0 20px 40px rgba(255, 192, 34, 0.3)",
                      border: "1px solid #ffc022",
                    },
                  }}
                >
                  <Box sx={{ mb: 2 }}>{card.icon}</Box>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ color: "#043a02", letterSpacing: "1px" }}
                  >
                    {card.title}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        {authMode !== "idle" && (
          <Box sx={{ width: "100%", maxWidth: 560 }}>
            <Box
              sx={{
                bgcolor: "rgba(255,255,255,0.97)",
                borderRadius: 4,
                p: 4,
                boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
                maxHeight: "88vh",
                overflowY: "auto",
              }}
            >
              {!shouldHideBackButton && (
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBack}
                  sx={{
                    mb: 2,
                    color: "#555",
                    pl: 0,
                    "&:hover": {
                      bgcolor: "transparent",
                      textDecoration: "underline",
                    },
                  }}
                >
                  {t("login.back")}
                </Button>
              )}

              {authMode === "farmer-choice" && (
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="h5"
                    sx={{ mb: 1, fontWeight: 700, color: "#1b5e20" }}
                  >
                    {t("login.farmerPortal")}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 4, color: "gray" }}>
                    {t("login.chooseOption")}
                  </Typography>
                  <Button
                    fullWidth
                    onClick={() => {
                      setErrors({});
                      setAadhaar("");
                      setAuthMode("farmer-verify-login");
                    }}
                    variant="contained"
                    sx={{
                      mb: 2,
                      py: 1.5,
                      bgcolor: "#1b5e20",
                      fontWeight: "bold",
                      fontSize: "1rem",
                      "&:hover": { bgcolor: "#002c07" },
                    }}
                  >
                    {t("login.loginBtn")}
                  </Button>
                  <Button
                    fullWidth
                    onClick={() => {
                      setErrors({});
                      setAadhaar("");
                      setAuthMode("farmer-register-verify");
                    }}
                    variant="outlined"
                    sx={{
                      py: 1.5,
                      color: "#1b5e20",
                      borderColor: "#1b5e20",
                      fontWeight: "bold",
                      fontSize: "1rem",
                      "&:hover": {
                        borderColor: "#1b5e20",
                        bgcolor: "rgba(27, 94, 32, 0.05)",
                      },
                    }}
                  >
                    {t("login.newRegistration")}
                  </Button>
                </Box>
              )}

              {authMode === "pc-choice" && (
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="h5"
                    sx={{ mb: 1, fontWeight: 700, color: "#1b5e20" }}
                  >
                    {t("login.pcPortal")}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 4, color: "gray" }}>
                    {t("login.chooseOption")}
                  </Typography>
                  <Button
                    fullWidth
                    onClick={() => {
                      setErrors({});
                      setMobile("");
                      setOtp("");
                      setShowOtpInput(false);
                      setActiveRole("Production Center");
                      setAuthMode("otp-login");
                    }}
                    variant="contained"
                    sx={{
                      mb: 2,
                      py: 1.5,
                      bgcolor: "#1b5e20",
                      fontWeight: "bold",
                      fontSize: "1rem",
                      "&:hover": { bgcolor: "#002c07" },
                    }}
                  >
                    {t("login.loginBtn")}
                  </Button>
                  <Button
                    fullWidth
                    onClick={() => {
                      setErrors({});
                      setPcRegData({ name: "", mobile: "", email: "" });
                      setAuthMode("pc-register-form");
                    }}
                    variant="outlined"
                    sx={{
                      py: 1.5,
                      color: "#1b5e20",
                      borderColor: "#1b5e20",
                      fontWeight: "bold",
                      fontSize: "1rem",
                      "&:hover": {
                        borderColor: "#1b5e20",
                        bgcolor: "rgba(27, 94, 32, 0.05)",
                      },
                    }}
                  >
                    {t("login.newRegistration")}
                  </Button>
                </Box>
              )}

              {authMode === "pc-register-form" && (
                <form onSubmit={handlePcRegisterSubmit}>
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 3,
                      fontWeight: 700,
                      color: "#1b5e20",
                      textAlign: "center",
                    }}
                  >
                    {t("login.pcRegistration")}
                  </Typography>

                  {errors.submit && (
                    <Typography
                      variant="body2"
                      color="error"
                      sx={{
                        mb: 2,
                        p: 1,
                        bgcolor: "#ffebee",
                        borderRadius: 1,
                        textAlign: "center",
                      }}
                    >
                      {errors.submit}
                    </Typography>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2.5,
                    }}
                  >
                    <TextField
                      fullWidth
                      variant="standard"
                      label="Username"
                      value={pcRegData.username}
                      onChange={(e) =>
                        handlePcRegChange("username", e.target.value)
                      }
                      error={!!errors.username}
                      helperText={errors.username || " "}
                      sx={textFieldStyles}
                    />

                    <MobileInput
                      value={pcRegData.mobile}
                      onChange={(val) => handlePcRegChange("mobile", val)}
                      error={!!errors.mobile}
                      helperText={errors.mobile || " "}
                      label={t("login.mobileLabel")}
                    />

                    <TextField
                      fullWidth
                      required
                      variant="standard"
                      label={t("login.emailAddress")}
                      type="email"
                      value={pcRegData.email}
                      onChange={(e) =>
                        handlePcRegChange("email", e.target.value)
                      }
                      error={!!errors.email}
                      helperText={errors.email || " "}
                      sx={textFieldStyles}
                    />

                    <button type="submit" disabled={loading} style={btnStyle}>
                      {loading && (
                        <CircularProgress size={20} color="inherit" />
                      )}

                      {loading ? t("login.registering") : t("login.register")}
                    </button>
                  </Box>
                </form>
              )}

              {authMode === "farmer-verify-login" && (
                <form onSubmit={handleFarmerLogin}>
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 1,
                      fontWeight: 700,
                      color: "#1b5e20",
                      textAlign: "center",
                    }}
                  >
                    {t("login.farmerLoginTitle")}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mb: 3, color: "gray", textAlign: "center" }}
                  >
                    {t("login.enterRegisteredAadhaar")}
                  </Typography>
                  <TextField
                    fullWidth
                    required
                    variant="standard"
                    label="Enter 12-digit Aadhaar Number"
                    value={aadhaar}
                    onChange={(e) => {
                      setAadhaar(
                        e.target.value.replace(/[^0-9]/g, "").slice(0, 12),
                      );
                      if (errors.aadhaar)
                        setErrors((prev) => ({ ...prev, aadhaar: "" }));
                    }}
                    error={!!errors.aadhaar}
                    helperText={errors.aadhaar || " "}
                    sx={textFieldStyles}
                    disabled={loading}
                    InputProps={{ inputProps: { maxLength: 12 } }}
                  />
                  <button type="submit" disabled={loading} style={btnStyle}>
                    {loading && <CircularProgress size={20} color="inherit" />}
                    {loading ? t("login.verifying") : t("login.verifyAndLogin")}
                  </button>
                </form>
              )}

              {authMode === "farmer-register-verify" && (
                <form onSubmit={handleCheckAadharForRegistration}>
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 1,
                      fontWeight: 700,
                      color: "#1b5e20",
                      textAlign: "center",
                    }}
                  >
                    {t("login.newRegTitle")}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mb: 3, color: "gray", textAlign: "center" }}
                  >
                    {t("login.enterAadhaarToCheck")}
                  </Typography>
                  <TextField
                    fullWidth
                    required
                    variant="standard"
                    label="Enter 12-digit Aadhaar Number"
                    value={aadhaar}
                    onChange={(e) => {
                      setAadhaar(
                        e.target.value.replace(/[^0-9]/g, "").slice(0, 12),
                      );
                      if (errors.regAadhaar)
                        setErrors((prev) => ({ ...prev, regAadhaar: "" }));
                    }}
                    error={!!errors.regAadhaar}
                    helperText={errors.regAadhaar || " "}
                    sx={textFieldStyles}
                    disabled={loading}
                    InputProps={{ inputProps: { maxLength: 12 } }}
                  />
                  <button type="submit" disabled={loading} style={btnStyle}>
                    {loading && <CircularProgress size={20} color="inherit" />}
                    {loading ? t("login.checking") : t("login.verifyAndLogin")}
                  </button>
                </form>
              )}

              {authMode === "farmer-confirm-details" && previewFarmerData && (
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 2,
                      fontWeight: 700,
                      color: "#1b5e20",
                      textAlign: "center",
                    }}
                  >
                    {t("login.verifyAndConfirm")}
                  </Typography>
                  {errors.confirm && (
                    <Typography
                      variant="body2"
                      color="error"
                      sx={{ mb: 2, p: 1, bgcolor: "#ffebee", borderRadius: 1 }}
                    >
                      {errors.confirm}
                    </Typography>
                  )}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      bgcolor: "#e8f5e9",
                      borderRadius: 2,
                      border: "1px solid #a5d6a7",
                      mb: 2,
                    }}
                  >
                    <Typography
                      color="#2e7d32"
                      fontWeight={700}
                      mb={1.5}
                      fontSize="0.9rem"
                    >
                      {t("login.farmerInfo")}
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={5}>
                        <Typography variant="body2" color="text.secondary">
                          {t("login.farmerId")}
                        </Typography>
                      </Grid>
                      <Grid item xs={7}>
                        <Typography variant="body2" fontWeight={600}>
                          {previewFarmerData.farmerId}
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography variant="body2" color="text.secondary">
                          {t("login.name")}
                        </Typography>
                      </Grid>
                      <Grid item xs={7}>
                        <Typography variant="body2">
                          {previewFarmerData.farmerName}
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography variant="body2" color="text.secondary">
                          {t("login.father")}
                        </Typography>
                      </Grid>
                      <Grid item xs={7}>
                        <Typography variant="body2">
                          {previewFarmerData.fatherName}
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography variant="body2" color="text.secondary">
                          {t("login.mobile")}
                        </Typography>
                      </Grid>
                      <Grid item xs={7}>
                        <Typography variant="body2">
                          {previewFarmerData.mobileNumber}
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography variant="body2" color="text.secondary">
                          {t("login.genderStatus")}
                        </Typography>
                      </Grid>
                      <Grid item xs={7}>
                        <Typography variant="body2">
                          {previewFarmerData.gender} /{" "}
                          {previewFarmerData.socialStatus}
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography variant="body2" color="text.secondary">
                          {t("login.dob")}
                        </Typography>
                      </Grid>
                      <Grid item xs={7}>
                        <Typography variant="body2">
                          {previewFarmerData.dob
                            ? new Date(
                                previewFarmerData.dob,
                              ).toLocaleDateString("en-IN")
                            : "N/A"}
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography variant="body2" color="text.secondary">
                          {t("login.address")}
                        </Typography>
                      </Grid>
                      <Grid item xs={7}>
                        <Typography variant="body2">
                          {previewFarmerData.address || "N/A"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                  {previewLands.length > 0 && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        bgcolor: "#e3f2fd",
                        borderRadius: 2,
                        border: "1px solid #90caf9",
                        mb: 3,
                      }}
                    >
                      <Typography
                        color="#1565c0"
                        fontWeight={700}
                        mb={1.5}
                        fontSize="0.9rem"
                      >
                        {t("login.landDetails")} ({previewLands.length}{" "}
                        {previewLands.length > 1
                          ? t("login.plots")
                          : t("login.plot")}
                        )
                      </Typography>
                      {previewLands.map((land, index) => (
                        <Box
                          key={land.landId || index}
                          sx={{
                            mb: index < previewLands.length - 1 ? 2 : 0,
                            pb: index < previewLands.length - 1 ? 2 : 0,
                            borderBottom:
                              index < previewLands.length - 1
                                ? "1px dashed #90caf9"
                                : "none",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: "#1565c0", fontWeight: 700 }}
                          >
                            {land.surveyNo}{" "}
                            {land.subDivNo ? `- ${land.subDivNo}` : ""}
                          </Typography>
                          <Grid container spacing={0.5} sx={{ mt: 0.5 }}>
                            <Grid item xs={5}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {t("login.village")}
                              </Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <Typography variant="body2">
                                {land.villageName}
                              </Typography>
                            </Grid>
                            <Grid item xs={5}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {t("login.area")}
                              </Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <Typography variant="body2" fontWeight={600}>
                                {land.area} {t("login.acres")}
                              </Typography>
                            </Grid>
                            <Grid item xs={5}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {t("login.type")}
                              </Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <Typography variant="body2">
                                {land.landType}
                              </Typography>
                            </Grid>
                            <Grid item xs={5}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {t("login.pattaNo")}
                              </Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <Typography variant="body2">
                                {land.pattaNo}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      ))}
                    </Paper>
                  )}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3, textAlign: "center", fontStyle: "italic" }}
                  >
                    {t("login.confirmAccountText")}
                  </Typography>
                  <button
                    type="button"
                    onClick={handleConfirmFarmerRegistration}
                    disabled={loading}
                    style={btnStyle}
                  >
                    {loading && <CircularProgress size={20} color="inherit" />}
                    {loading
                      ? t("login.creatingAccount")
                      : t("login.confirmAccountBtn")}
                  </button>
                </Box>
              )}

              {authMode === "farmer-register-form" && (
                <form onSubmit={handleFarmerRegisterSubmit}>
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 0.5,
                      fontWeight: 700,
                      color: "#1b5e20",
                      textAlign: "center",
                    }}
                  >
                    {t("login.nonFarmerReg")}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      textAlign: "center",
                      mb: 3,
                      p: 1.5,
                      borderRadius: 1,
                      fontWeight: "bold",
                      bgcolor: "#fff3e0",
                      color: "#ef6c00",
                      lineHeight: 1.4,
                    }}
                  >
                    Your Aadhaar: {aadhaar.replace(/(\d{4})/g, "$1 ").trim()}{" "}
                    {t("login.aadhaarNotRegistered")}
                  </Typography>
                  {errors.submit && (
                    <Typography
                      variant="body2"
                      color="error"
                      sx={{
                        mb: 2,
                        p: 1,
                        bgcolor: "#ffebee",
                        borderRadius: 1,
                        textAlign: "center",
                      }}
                    >
                      {errors.submit}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      maxHeight: "60vh",
                      overflowY: "scroll",
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                      "&::-webkit-scrollbar": { display: "none" },
                      display: "flex",
                      flexDirection: "column",
                      gap: 2.5,
                      pr: 1,
                    }}
                  >
                    <TextField
                      fullWidth
                      required
                      variant="standard"
                      label={t("login.nameField")}
                      value={regData.farmer_name}
                      onChange={(e) =>
                        handleRegChange("farmer_name", e.target.value)
                      }
                      error={!!errors.farmer_name}
                      helperText={errors.farmer_name || " "}
                      sx={textFieldStyles}
                    />
                    <MobileInput
                      value={regData.mobile_number}
                      onChange={(val) => handleRegChange("mobile_number", val)}
                      error={!!errors.mobile_number}
                      helperText={errors.mobile_number || " "}
                      label={t("login.mobileLabel")}
                    />
                    <Autocomplete
                      fullWidth
                      options={districts}
                      getOptionLabel={(option) => option.District_Name || ""}
                      value={
                        districts.find(
                          (d) => String(d.id) === String(regData.district_id),
                        ) || null
                      }
                      isOptionEqualToValue={(option, value) =>
                        String(option.id) === String(value?.id)
                      }
                      onChange={handleDistrictChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          variant="standard"
                          label={t("login.district")}
                          error={!!errors.district_id}
                          helperText={errors.district_id || " "}
                          sx={textFieldStyles}
                        />
                      )}
                    />
                    <Autocomplete
                      fullWidth
                      options={blocks}
                      getOptionLabel={(option) => option.Block_Name || ""}
                      value={
                        blocks.find(
                          (b) => String(b.id) === String(regData.block_id),
                        ) || null
                      }
                      isOptionEqualToValue={(option, value) =>
                        String(option.id) === String(value?.id)
                      }
                      onChange={handleBlockChange}
                      disabled={!regData.district_id || blocks.length === 0}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          variant="standard"
                          label={t("login.block")}
                          error={!!errors.block_id}
                          helperText={
                            !regData.district_id
                              ? "Select district first"
                              : errors.block_id || " "
                          }
                          sx={textFieldStyles}
                        />
                      )}
                    />
                    <Autocomplete
                      fullWidth
                      options={villages}
                      getOptionLabel={(option) => option.Village_Name || ""}
                      value={
                        villages.find(
                          (v) => String(v.id) === String(regData.village_id),
                        ) || null
                      }
                      isOptionEqualToValue={(option, value) =>
                        String(option.id) === String(value?.id)
                      }
                      onChange={handleVillageChange}
                      disabled={!regData.block_id || villages.length === 0}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          variant="standard"
                          label={t("login.village")}
                          error={!!errors.village_id}
                          helperText={
                            !regData.block_id
                              ? "Select block first"
                              : errors.village_id || " "
                          }
                          sx={textFieldStyles}
                        />
                      )}
                    />
                    <TextField
                      fullWidth
                      required
                      variant="standard"
                      label={t("login.fullAddress")}
                      multiline
                      rows={2}
                      value={regData.address}
                      onChange={(e) =>
                        handleRegChange("address", e.target.value)
                      }
                      error={!!errors.address}
                      helperText={errors.address || " "}
                      sx={textFieldStyles}
                    />
                    <TextField
                      fullWidth
                      required
                      variant="standard"
                      label={t("login.purposeField")}
                      placeholder={t("login.purposePlaceholder")}
                      multiline
                      rows={2}
                      value={regData.purpose}
                      onChange={(e) =>
                        handleRegChange("purpose", e.target.value)
                      }
                      error={!!errors.purpose}
                      helperText={errors.purpose || " "}
                      sx={textFieldStyles}
                    />
                    <button type="submit" disabled={loading} style={btnStyle}>
                      {loading && (
                        <CircularProgress size={20} color="inherit" />
                      )}
                      {loading
                        ? t("login.fetchingLocation")
                        : t("login.registerAndLogin")}
                    </button>
                  </Box>
                </form>
              )}

              {authMode === "otp-login" && (
                <form
                  onSubmit={
                    showOtpInput && timer > 0 ? handleVerifyOtp : handleSendOtp
                  }
                >
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 0.5,
                      fontWeight: 700,
                      color: "#1b5e20",
                      textAlign: "center",
                    }}
                  >
                    {activeRole === "Production Center"
                      ? t("login.pcLoginTitle")
                      : t("login.officeLoginTitle")}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mb: 3, color: "gray", textAlign: "center" }}
                  >
                    {activeRole === "Production Center"
                      ? t("login.pcLoginSubtitle")
                      : t("login.officeLoginSubtitle")}
                  </Typography>

                  {!showOtpInput && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2.5,
                      }}
                    >
                      {activeRole === "Production Center" && (
                        <TextField
                          fullWidth
                          variant="standard"
                          label="Username"
                          value={pcUsername}
                          onChange={(e) => setPcUsername(e.target.value)}
                          error={!!errors.username}
                          helperText={errors.username || " "}
                          sx={textFieldStyles}
                        />
                      )}
                      <MobileInput
                        value={mobile}
                        onChange={setMobile}
                        error={!!errors.mobile}
                        helperText={errors.mobile || " "}
                        label={t("login.mobileLabel")}
                      />
                      <button type="submit" disabled={loading} style={btnStyle}>
                        {loading && (
                          <CircularProgress size={20} color="inherit" />
                        )}
                        {loading ? t("login.processing") : t("login.sendOtp")}
                      </button>
                    </Box>
                  )}

                  {showOtpInput && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2.5,
                      }}
                    >
                      <MobileInput
                        value={mobile}
                        onChange={setMobile}
                        error={!!errors.mobile}
                        helperText={errors.mobile || " "}
                        label={t("login.mobileLabel")}
                        disabled
                      />
                      <TextField
                        fullWidth
                        required
                        variant="standard"
                        label={t("login.enterOtp")}
                        value={otp}
                        onChange={(e) => {
                          setOtp(
                            e.target.value.replace(/[^0-9]/g, "").slice(0, 6),
                          );
                          if (errors.otp)
                            setErrors((prev) => ({ ...prev, otp: "" }));
                        }}
                        error={!!errors.otp}
                        helperText={
                          errors.otp ||
                          (timer > 0
                            ? `${t("login.otpExpiresIn")} ${timer}s`
                            : " ")
                        }
                        sx={textFieldStyles}
                        InputProps={{ inputProps: { maxLength: 6 } }}
                      />
                      {timer === 0 && (
                        <Button
                          fullWidth
                          onClick={handleSendOtp}
                          disabled={loading}
                          sx={{
                            textTransform: "none",
                            color: "#1b5e20",
                            fontWeight: 600,
                            "&:hover": { bgcolor: "rgba(27,94,32,0.05)" },
                          }}
                        >
                          {t("login.resendOtp")}
                        </Button>
                      )}
                      <button type="submit" disabled={loading} style={btnStyle}>
                        {loading && (
                          <CircularProgress size={20} color="inherit" />
                        )}
                        {loading ? t("login.verifying") : t("login.verifyOtp")}
                      </button>
                    </Box>
                  )}
                </form>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Login;
